import { useCallback, useEffect, useState, type ReactElement } from "react";

import { api } from "../api/client";
import type { AdminDeletionRequest, AdminSubstudy } from "../api/types";
import { AppShell } from "../components/AppShell";
import { Tabs, TabPanel, type TabDef } from "../components/Tabs";
import { AuditTab } from "../components/admin/AuditTab";
import { BatchUploadCard } from "../components/admin/BatchUploadCard";
import { ExportsTab } from "../components/admin/ExportsTab";
import { OverviewTab } from "../components/admin/OverviewTab";
import { ParticipantsTab } from "../components/admin/ParticipantsTab";
import { StudiesTab } from "../components/admin/StudiesTab";

const TABS: TabDef[] = [
  { id: "overview", label: "Overview", icon: "fas fa-chart-simple" },
  { id: "studies", label: "Substudies", icon: "fas fa-flask" },
  { id: "upload", label: "Batch upload", icon: "fas fa-layer-group" },
  { id: "participants", label: "Participants", icon: "fas fa-users" },
  { id: "audit", label: "Audit", icon: "fas fa-list-check" },
  { id: "exports", label: "Exports", icon: "fas fa-download" },
];

// The study-team console shell: app bar + tab bar. Each tab fetches its own
// data on mount; the ONLY hoisted state is the substudy list, shared by
// most tabs' dropdowns and refreshed when StudiesTab creates a substudy. Mirrors
// the Collab shell.
export function Admin(): ReactElement {
  const [activeTab, setActiveTab] = useState("overview");
  const [substudies, setSubstudies] = useState<AdminSubstudy[]>([]);

  const [pendingDeletions, setPendingDeletions] = useState(0);

  const reloadSubstudies = useCallback(async () => {
    try {
      setSubstudies(await api<AdminSubstudy[]>("GET", "/admin/substudies"));
    } catch {
      setSubstudies([]);
    }
  }, []);

  useEffect(() => {
    void reloadSubstudies();
  }, [reloadSubstudies]);

  // Hoisted to the shell, unlike every other tab's data: a permanent-deletion
  // request is a compliance clock the admin must see from wherever they are, and
  // ParticipantsTab's own copy of the queue is invisible from the other five
  // tabs. On failure the badge stays hidden rather than showing a wrong count —
  // ParticipantsTab surfaces the load error itself.
  const reloadPendingDeletions = useCallback(async () => {
    try {
      const rows = await api<AdminDeletionRequest[]>("GET", "/admin/deletion-requests");
      setPendingDeletions(
        Array.isArray(rows) ? rows.filter((r) => r.status === "pending").length : 0,
      );
    } catch {
      setPendingDeletions(0);
    }
  }, []);

  useEffect(() => {
    void reloadPendingDeletions();
  }, [reloadPendingDeletions]);

  const tabs = TABS.map((tab) =>
    tab.id === "participants" ? { ...tab, badge: pendingDeletions } : tab,
  );

  return (
    <AppShell brand="FaceAge Admin">
      <Tabs tabs={tabs} active={activeTab} onSelect={setActiveTab} label="Admin sections" />
      {activeTab === "overview" ? <OverviewTab /> : null}
      {activeTab === "studies" ? (
        <StudiesTab substudies={substudies} reloadSubstudies={reloadSubstudies} />
      ) : null}
      {activeTab === "upload" ? (
        <TabPanel id="upload" card={false}>
          <BatchUploadCard substudies={substudies} />
        </TabPanel>
      ) : null}
      {activeTab === "participants" ? (
        <ParticipantsTab substudies={substudies} onDeletionsResolved={reloadPendingDeletions} />
      ) : null}
      {activeTab === "audit" ? <AuditTab substudies={substudies} /> : null}
      {activeTab === "exports" ? <ExportsTab substudies={substudies} /> : null}
    </AppShell>
  );
}
